import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Button, SubmitButton } from "../shared/Button"
import { useMutation } from "react-query"
import { mutationRequest } from "../../util/request"
import { LegalLanguage } from "./LegalLanguage"
import type { AuthFlowData } from "./AuthenticationDialog"

interface AuthenticationFormProps {
  setModalState: (state: "auth" | "register" | "reset_password") => void
  validationError: (data: AuthFlowData) => string | null
  closeDialog: () => void
}

interface AuthFormValues {
  email: string
  password: string
  time_zone: string
}

export const AuthenticationForm: React.FC<AuthenticationFormProps> = ({
  setModalState,
  validationError,
  closeDialog,
}) => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<AuthFormValues>({
    defaultValues: {
      email: new URLSearchParams(window.location.search).get("email") ?? "",
      password: "",
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  })

  const [authErrorMessage, setAuthErrorMessage] = useState("")
  const [loginLinkValidMessage, setLoginLinkValidMessage] = useState<
    string | null
  >(null)
  const mutation = useMutation(mutationRequest)
  const isSubmitting = mutation.isLoading
  const [flow, setFlow] = useState<"authenticate" | "login_link">(
    "authenticate",
  )

  const onSubmitAuthentication = async (data: AuthFormValues) => {
    setFlow("authenticate")
    setAuthErrorMessage("")

    const authData: AuthFlowData = {
      flow: "authenticate",
      current_location: window.location.href,
      referral_token: localStorage.getItem("referral_token") ?? undefined,
      user: {
        email: data.email,
        password: data.password,
        time_zone: data.time_zone,
        remember_me: true,
      },
    } as AuthFlowData & { current_location: string }

    const errorMessage = validationError(authData)
    if (errorMessage) {
      setAuthErrorMessage(errorMessage)
      return
    }

    try {
      mutation.mutate({
        options: { method: "POST" },
        path: `/api/v1/users`,
        data: authData,
      })
    } catch {
      setAuthErrorMessage("Sorry, there was an unknown error")
    }
  }

  const handleSendLoginLink = async (e: React.MouseEvent) => {
    e.preventDefault()
    setFlow("login_link")
    setAuthErrorMessage("")

    const email = getValues("email")

    const loginLinkData = {
      flow: "login_link",
      current_location: window.location.href,
      user: { email },
    }

    const errorMessage = validationError(loginLinkData as AuthFlowData)
    if (errorMessage) {
      setAuthErrorMessage(errorMessage)
      return
    }

    try {
      mutation.mutate({
        options: { method: "POST" },
        path: `/api/v1/users`,
        data: loginLinkData,
      })
    } catch {
      setAuthErrorMessage("Sorry, there was an unknown error")
    }
  }

  useEffect(() => {
    if (mutation.status === "success") {
      const data = mutation.data as {
        success?: boolean
        token?: string
        redirect_location?: string
        errors?: { error: string }
      }
      if (data.success) {
        if (flow === "authenticate") {
          localStorage.setItem("jwt_token", data.token ?? "")
          closeDialog()
          const redirectUrl = new URL(
            data.redirect_location ?? "/",
            window.location.origin,
          )
          redirectUrl.searchParams.delete("show-dialog")
          redirectUrl.searchParams.delete("email")
          window.location.href = redirectUrl.toString()
        } else {
          setLoginLinkValidMessage(
            "Check your email (spam) for login link instructions from bandnada.com",
          )
        }
      } else {
        setAuthErrorMessage(data.errors?.error ?? "Unknown error")
      }
    } else if (mutation.status === "error") {
      setAuthErrorMessage("Sorry, there was an unknown error")
    }
  }, [mutation.status, mutation.data, flow, closeDialog])

  return (
    <>
      <div className="lg:col-span-9 col-span-12 lg:pr-6">
        <h1 className="border-b-4 border-black font-bold mb-4 text-xl uppercase">
          Sign In
        </h1>
        {authErrorMessage ? (
          <div className="text-red-700 text-sm mb-2">{authErrorMessage}</div>
        ) : null}
        {loginLinkValidMessage ? (
          <div className="text-green-700 text-sm mb-2">
            {loginLinkValidMessage}
          </div>
        ) : null}
        <form onSubmit={handleSubmit(onSubmitAuthentication)}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-xs uppercase font-bold"
              htmlFor="email"
            >
              Email Address
            </label>
            <input
              className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="email"
              placeholder="Email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="text-red-700 text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="mb-6">
            <label
              className="block text-gray-700 text-xs uppercase font-bold"
              htmlFor="password"
            >
              Password
            </label>
            <input
              className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="password"
              placeholder="******************"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
                maxLength: {
                  value: 24,
                  message: "Password must be no more than 24 characters",
                },
              })}
            />
            {errors.password && (
              <p className="text-red-700 text-xs mt-1">
                {errors.password.message}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <SubmitButton
              className="disabled:cursor-not-allowed"
              active={!isSubmitting}
            >
              {isSubmitting && flow === "authenticate"
                ? "Signing In..."
                : "Sign In"}
            </SubmitButton>
          </div>
          <div className="text-center my-4 font-bold text-gray-700">
            -- Or --
          </div>
          <div className="flex items-center justify-between">
            <Button
              bg="bg-blue-500"
              className="block w-full uppercase disabled:cursor-not-allowed"
              disabled={isSubmitting}
              onClick={(e) => handleSendLoginLink(e)}
            >
              {isSubmitting && flow === "login_link"
                ? "Sending Login Link..."
                : "Send Login Link"}
            </Button>
          </div>
          <p className="italic leading-tight pt-2 text-gray-700 text-sm">
            We'll send you an email now with a link that logs you in
            automatically. Easy as 1,2. Or you can{" "}
            <button
              className="focus:outline-none text-blue-600 text-sm inline disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              disabled={isSubmitting}
              onClick={() => setModalState("reset_password")}
            >
              Reset Your Password (old-school).
            </button>
          </p>
        </form>
      </div>
      <div className="lg:col-span-3 col-span-12 lg:mt-0 mt-16 flex flex-col justify-between">
        <div>
          <h1 className="border-b-4 border-black font-bold mb-4 text-xl text-right">
            New to bandNada?
          </h1>
          <div className="mt-8 text-right">
            <Button
              className="block ml-auto disabled:cursor-not-allowed"
              disabled={isSubmitting}
              onClick={() => setModalState("register")}
            >
              Sign Up for Free
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <LegalLanguage />
        </div>
      </div>
    </>
  )
}
